import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Icon from "../ui/Icon";

// ─── Animation variants ───────────────────────────────────────────────────────
const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] },
  }),
};

const stagger = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.3 },
  },
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.85 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
  },
};

// ─── Feature data ─────────────────────────────────────────────────────────────
const FEATURES = [
  {
    icon: "target",
    title: "Smart Goal Setting",
    description:
      "Set personalized goals for fat loss, muscle gain, recomposition, or maintenance. Our Mifflin-St Jeor calculator builds your perfect macro split.",
    color: "from-indigo-500 to-indigo-600",
    glow: "rgba(99,102,241,0.2)",
  },
  {
    icon: "flame",
    title: "Calorie & Macro Tracking",
    description:
      "Track calories, protein, carbs, and fats in real time with animated progress rings. Never guess your daily intake again.",
    color: "from-orange-500 to-red-500",
    glow: "rgba(249,115,22,0.2)",
  },
  {
    icon: "search",
    title: "Instant Food Search",
    description:
      "Search 300,000+ foods from the USDA database with instant results. Find any food, any brand, in milliseconds.",
    color: "from-cyan-500 to-blue-500",
    glow: "rgba(6,182,212,0.2)",
  },
  {
    icon: "salad",
    title: "Custom Bowl Builder",
    description:
      "Drag and drop ingredients to build custom bowls. Save your favorites and log them with one tap.",
    color: "from-green-500 to-emerald-500",
    glow: "rgba(16,185,129,0.2)",
  },
  {
    icon: "analytics",
    title: "Visual Analytics",
    description:
      "7, 14, and 30-day charts for calorie adherence, macro trends, and weight tracking. See your progress unfold.",
    color: "from-purple-500 to-violet-500",
    glow: "rgba(139,92,246,0.2)",
  },
  {
    icon: "zap",
    title: "Adaptive Meal Plans",
    description:
      "Choose 3–6 meals per day. Your schedule adapts from Early Fuel to Twilight Graze — matching your lifestyle.",
    color: "from-amber-500 to-yellow-500",
    glow: "rgba(245,158,11,0.2)",
  },
];

// ─── Stats ────────────────────────────────────────────────────────────────────
const STATS = [
  { value: "300K+", label: "Foods in Database" },
  { value: "6", label: "Flexible Meal Slots" },
  { value: "100%", label: "Free to Use" },
];

// ─── Component ────────────────────────────────────────────────────────────────
export default function LandingPage() {
  return (
    <div className="min-h-screen bg-black relative overflow-hidden">
      {/* ── Background effects ── */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[600px] bg-indigo-600/[0.07] rounded-full blur-[120px]" />
        <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-purple-600/[0.05] rounded-full blur-[100px]" />
        <div className="absolute top-1/2 left-0 w-[400px] h-[400px] bg-cyan-600/[0.04] rounded-full blur-[80px]" />
      </div>

      {/* ── Navigation ── */}
      <nav className="relative z-10 flex items-center justify-between px-6 sm:px-10 lg:px-16 py-5 max-w-7xl mx-auto">
        <motion.div
          className="flex items-center gap-3"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white shadow-lg shadow-indigo-500/25">
            <Icon name="salad" size={20} />
          </div>
          <span className="text-xl font-bold text-white tracking-tight">Shredd</span>
        </motion.div>

        <motion.div
          className="flex items-center gap-3"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Link
            to="/login"
            className="px-4 py-2 text-sm font-medium text-slate-300 hover:text-white transition-colors"
          >
            Log In
          </Link>
          <Link
            to="/register"
            className="px-5 py-2.5 text-sm font-semibold text-white rounded-xl bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-400 hover:to-purple-500 shadow-lg shadow-indigo-500/25 hover:shadow-indigo-500/40 transition-all duration-300"
          >
            Get Started
          </Link>
        </motion.div>
      </nav>

      {/* ── Hero Section ── */}
      <section className="relative z-10 px-6 sm:px-10 lg:px-16 pt-16 sm:pt-24 pb-20 max-w-7xl mx-auto">
        <div className="text-center max-w-4xl mx-auto">
          {/* Badge */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            custom={0}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-indigo-500/10 border border-indigo-500/20 mb-8"
          >
            <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
            <span className="text-xs font-medium text-indigo-300">
              Track smarter. Eat better. Get shredded.
            </span>
          </motion.div>

          {/* Headline */}
          <motion.h1
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            custom={1}
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold text-white leading-[1.1] tracking-tight"
          >
            Your nutrition,{" "}
            <span className="gradient-text">mastered.</span>
          </motion.h1>

          {/* Subheadline */}
          <motion.p
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            custom={2}
            className="mt-6 text-lg sm:text-xl text-slate-400 max-w-2xl mx-auto leading-relaxed"
          >
            The all-in-one diet tracker that combines precision macro counting,
            intelligent food search, and beautiful analytics — so you can focus
            on crushing your goals.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            custom={3}
            className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <Link
              to="/register"
              className="group w-full sm:w-auto px-8 py-4 text-base font-semibold text-white rounded-2xl bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-400 hover:to-purple-500 shadow-xl shadow-indigo-500/25 hover:shadow-indigo-500/40 transition-all duration-300 flex items-center justify-center gap-2"
            >
              Start Tracking Free
              <svg
                className="w-4 h-4 group-hover:translate-x-1 transition-transform"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </Link>
            <Link
              to="/login"
              className="w-full sm:w-auto px-8 py-4 text-base font-medium text-slate-300 hover:text-white rounded-2xl border border-white/10 hover:border-white/20 bg-white/[0.03] hover:bg-white/[0.06] transition-all duration-300 flex items-center justify-center gap-2"
            >
              I have an account
            </Link>
          </motion.div>

          {/* Stats */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            custom={4}
            className="mt-16 grid grid-cols-3 gap-6 max-w-md mx-auto"
          >
            {STATS.map((stat) => (
              <div key={stat.label} className="text-center">
                <p className="text-2xl sm:text-3xl font-bold text-white">{stat.value}</p>
                <p className="text-xs sm:text-sm text-slate-500 mt-1">{stat.label}</p>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Hero Visual — Floating dashboard mockup */}
        <motion.div
          variants={scaleIn}
          initial="hidden"
          animate="visible"
          className="mt-20 relative max-w-3xl mx-auto"
        >
          <div className="absolute -inset-4 bg-gradient-to-r from-indigo-500/20 via-purple-500/20 to-cyan-500/20 rounded-3xl blur-2xl opacity-60" />
          <div className="relative glass-strong rounded-2xl p-6 sm:p-8">
            {/* Mock progress rings */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <p className="text-sm text-slate-400">Today's Progress</p>
                <p className="text-2xl font-bold text-white mt-1">1,847 <span className="text-sm font-normal text-slate-500">/ 2,200 kcal</span></p>
              </div>
              <div className="flex gap-3">
                {[
                  { label: "P", val: "84%", color: "text-cyan-400" },
                  { label: "C", val: "72%", color: "text-amber-400" },
                  { label: "F", val: "68%", color: "text-pink-400" },
                ].map((m) => (
                  <div key={m.label} className="text-center">
                    <p className={`text-lg font-bold ${m.color}`}>{m.val}</p>
                    <p className="text-xs text-slate-500">{m.label}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Mock meal rows */}
            <div className="space-y-2">
              {[
                { meal: "Breakfast", icon: "sunrise", cal: "520 kcal", items: "Oatmeal, Eggs, Banana" },
                { meal: "Lunch", icon: "sun", cal: "680 kcal", items: "Chicken Bowl, Rice, Avocado" },
                { meal: "Dinner", icon: "sunset", cal: "647 kcal", items: "Salmon, Sweet Potato, Greens" },
              ].map((row) => (
                <div
                  key={row.meal}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl"
                  style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.04)" }}
                >
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center text-indigo-300 flex-shrink-0"
                    style={{ background: "rgba(99,102,241,0.1)" }}
                  >
                    <Icon name={row.icon} size={16} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white">{row.meal}</p>
                    <p className="text-xs text-slate-500 truncate">{row.items}</p>
                  </div>
                  <span className="text-xs font-semibold text-indigo-400 flex-shrink-0">{row.cal}</span>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </section>

      {/* ── Features Section ── */}
      <section className="relative z-10 px-6 sm:px-10 lg:px-16 py-24 max-w-7xl mx-auto">
        <motion.div
          className="text-center mb-16"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          custom={0}
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-white">
            Everything you need to{" "}
            <span className="gradient-text">get shredded</span>
          </h2>
          <p className="mt-4 text-lg text-slate-400 max-w-xl mx-auto">
            Built for anyone serious about their nutrition — from first-time trackers to seasoned athletes.
          </p>
        </motion.div>

        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5"
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
        >
          {FEATURES.map((feature) => (
            <motion.div
              key={feature.title}
              variants={fadeUp}
              className="glass-card rounded-2xl p-6 group cursor-default"
            >
              <div
                className={`w-12 h-12 rounded-xl bg-gradient-to-br ${feature.color} flex items-center justify-center text-white mb-4 shadow-lg`}
                style={{ boxShadow: `0 8px 24px ${feature.glow}` }}
              >
                <Icon name={feature.icon} size={22} />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
              <p className="text-sm text-slate-400 leading-relaxed">{feature.description}</p>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* ── How It Works Section ── */}
      <section className="relative z-10 px-6 sm:px-10 lg:px-16 py-24 max-w-7xl mx-auto">
        <motion.div
          className="text-center mb-16"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          custom={0}
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-white">
            Up and running in{" "}
            <span className="gradient-text">3 minutes</span>
          </h2>
          <p className="mt-4 text-lg text-slate-400 max-w-xl mx-auto">
            No complicated setup. Just a quick onboarding flow tailored to your body and goals.
          </p>
        </motion.div>

        <motion.div
          className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto"
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
        >
          {[
            {
              step: "01",
              title: "Set Your Goals",
              description: "Tell us your goal, metrics, and activity level. We calculate your perfect macro targets.",
              icon: "target",
            },
            {
              step: "02",
              title: "Track Your Meals",
              description: "Search and log foods in seconds. Build custom bowls. See real-time progress.",
              icon: "utensils",
            },
            {
              step: "03",
              title: "Crush Your Goals",
              description: "Watch your progress unfold with beautiful charts. Adjust and optimize as you go.",
              icon: "analytics",
            },
          ].map((item) => (
            <motion.div
              key={item.step}
              variants={fadeUp}
              className="text-center"
            >
              <div className="relative inline-flex items-center justify-center mb-5">
                <div className="absolute inset-0 w-16 h-16 rounded-2xl bg-indigo-500/10 blur-xl" />
                <div className="relative w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500/20 to-purple-500/20 border border-indigo-500/20 flex items-center justify-center text-indigo-300">
                  <Icon name={item.icon} size={24} />
                </div>
                <span className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-indigo-500 flex items-center justify-center text-[10px] font-bold text-white">
                  {item.step}
                </span>
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
              <p className="text-sm text-slate-400 leading-relaxed">{item.description}</p>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* ── Final CTA Section ── */}
      <section className="relative z-10 px-6 sm:px-10 lg:px-16 py-24 max-w-7xl mx-auto">
        <motion.div
          className="relative overflow-hidden rounded-3xl"
          variants={scaleIn}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-indigo-600/30 to-purple-600/30" />
          <div className="absolute inset-0 glass-strong" />
          <div className="relative px-8 sm:px-12 py-16 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Ready to transform your nutrition?
            </h2>
            <p className="text-lg text-slate-300 mb-8 max-w-lg mx-auto">
              Join Shredd today — set up in minutes, track for life. No credit card required.
            </p>
            <Link
              to="/register"
              className="inline-flex items-center gap-2 px-8 py-4 text-base font-semibold text-white rounded-2xl bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-400 hover:to-purple-500 shadow-xl shadow-indigo-500/25 hover:shadow-indigo-500/40 transition-all duration-300"
            >
              Get Started — It's Free
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </Link>
          </div>
        </motion.div>
      </section>

      {/* ── Footer ── */}
      <footer className="relative z-10 px-6 sm:px-10 lg:px-16 py-8 max-w-7xl mx-auto border-t border-white/[0.04]">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white">
              <Icon name="salad" size={14} />
            </div>
            <span className="text-sm font-semibold text-slate-400">Shredd</span>
          </div>
          <p className="text-xs text-slate-600">
            © {new Date().getFullYear()} Shredd. Built for a healthier you.
          </p>
        </div>
      </footer>
    </div>
  );
}
